import React from 'react';
import Seo from '@/components/Seo';
import { Icon, Reveal, Section } from '@/components/ui';
import { companyContent } from '@/content/company';
import AboutUs from '@/components/about-us/AboutUs.tsx';

/**
 * Standalone "Company" page template (§11 of
 * `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md`) — a fuller story/history
 * page than the compact home-page `AboutUs` section. Content lives in
 * `src/content/company.ts`; delete this file + its route in `App.tsx` (and
 * the `/company` entry in `siteConfig.pages`) if a buyer only needs a
 * one-pager.
 *
 * The `AboutUs` section is rendered at the top of this page as well as on
 * the home page (see `Home.tsx`) — it is not removed from the home page,
 * only linked to via the "Company" nav entry instead of its own nav link.
 */
const Company: React.FC = () => {
    return (
        <>
            <Seo title={companyContent.title} path="/company" breadcrumbs={[]} />
            <AboutUs />
            <Section id="company-intro" className="page-hero" contained>
                <h1 className="page-hero-title">{companyContent.title}</h1>
                <p className="page-hero-intro">{companyContent.intro}</p>
            </Section>

            <Section id="company-highlights" className="company-highlights" contained>
                <div className="trust-icons">
                    {companyContent.highlights.map((item, index) => (
                        <Reveal
                            as="div"
                            className="trust-item"
                            key={item.title}
                            delay={index * 100}
                        >
                            <div className="icon-circle">
                                <Icon name={item.icon} />
                            </div>
                            <h4>{item.title}</h4>
                            <p>{item.description}</p>
                        </Reveal>
                    ))}
                </div>
            </Section>

            <Section
                id="company-milestones"
                className="company-milestones"
                title="Milestones"
                contained
            >
                <ol className="timeline">
                    {companyContent.milestones.map((milestone, index) => (
                        <Reveal
                            as="li"
                            className="timeline-item"
                            key={`${milestone.year}-${index}`}
                            delay={index * 100}
                        >
                            <span className="timeline-year">{milestone.year}</span>
                            <span className="timeline-description">{milestone.description}</span>
                        </Reveal>
                    ))}
                </ol>
            </Section>
        </>
    );
};

export default Company;
